// src/components/licence/api/MyLicences.js
// 「自分が管理している User のライセンスだけ」を listLicences(Scan + filter) で取得するヘルパー

import { API, graphqlOperation } from 'aws-amplify';
import { listLicences as ListLicences } from './queries';
import { isUndefined, addContains } from './../../../shared/utils/misc';

/**
 * SELECT: ライセンスリスト取得（自分が管理するユーザのみ）
 *
 * @param {*} argFilterObject  { myUserIds, ...filter } を想定
 * @param {*} argNextToken     次ページ用 nextToken
 * @param {*} argLimit         1回の取得件数（デフォルト 500）
 */
export async function getMyLicences(
  { myUserIds, ...argFilter } = {},
  argNextToken = null,
  argLimit = 500
) {
  // userId がまったく無い場合は、全件 Scan を避けるため空を返す
  if (!Array.isArray(myUserIds) || myUserIds.length === 0) {
    return {
      items: [],
      nextToken: null,
    };
  }

  // listLicences の引数
  const queryArgument = {
    limit: argLimit,
    nextToken: argNextToken,
  };

  // filter: and 配列を組み立てる
  let queryFilter = {
    and: [],
  };

  // 1) 「自分が管理するユーザの userId 」に絞る
  queryFilter.and.push({
    or: myUserIds.map((id) => ({ userId: { eq: id } })),
  });

  // 2) active
  if (!isUndefined(argFilter.active)) {
    queryFilter.and.push({
      active: { eq: argFilter.active },
    });
  }

  // 3) type
  if (!isUndefined(argFilter.type)) {
    queryFilter.and.push({
      type: { eq: argFilter.type },
    });
  }

  // 4) period
  if (!isUndefined(argFilter.period)) {
    queryFilter.and.push({
      period: { eq: argFilter.period },
    });
  }

  // 5) licenceKey（部分一致検索）
  addContains(queryFilter.and, 'licenceKey', argFilter.licenceKey, {
    minLen: 1,
  });

  // 6) uuid（部分一致検索）
  addContains(queryFilter.and, 'uuid', argFilter.uuid, {
    minLen: 1,
  });

  // 7) acsVersionId（部分一致検索）
    addContains(queryFilter.and, 'acsVersionId', argFilter.acsVersionId, {
    minLen: 1,
  });

  // 8) updatedAt: between / gt / lt のいずれか1つ
  if (!isUndefined(argFilter.updatedAtGt) || !isUndefined(argFilter.updatedAtLt)) {
    const from = argFilter.updatedAtGt;
    const to = argFilter.updatedAtLt;

    if (!isUndefined(from) && !isUndefined(to)) {
      const a = String(from);
      const b = String(to);
      queryFilter.and.push({
        updatedAt: { between: a <= b ? [a, b] : [b, a] },
      });
    } else if (!isUndefined(from)) {
      queryFilter.and.push({
        updatedAt: { gt: from },
      });
    } else if (!isUndefined(to)) {
      queryFilter.and.push({
        updatedAt: { lt: to },
      });
    }
  }

  // 9) createdAt: between / gt / lt
  if (!isUndefined(argFilter.createdAtGt) || !isUndefined(argFilter.createdAtLt)) {
    const from = argFilter.createdAtGt;
    const to = argFilter.createdAtLt;

    if (!isUndefined(from) && !isUndefined(to)) {
      const a = String(from);
      const b = String(to);
      queryFilter.and.push({
        createdAt: { between: a <= b ? [a, b] : [b, a] },
      });
    } else if (!isUndefined(from)) {
      queryFilter.and.push({
        createdAt: { gt: from },
      });
    } else if (!isUndefined(to)) {
      queryFilter.and.push({
        createdAt: { lt: to },
      });
    }
  }

  // 10) 有効期限 subscriptionExpiryDateAt: between / gt / lt
  if (
    !isUndefined(argFilter.subscriptionExpiryDateAtGt) ||
    !isUndefined(argFilter.subscriptionExpiryDateAtLt)
  ) {
    const from = argFilter.subscriptionExpiryDateAtGt;
    const to = argFilter.subscriptionExpiryDateAtLt;

    if (!isUndefined(from) && !isUndefined(to)) {
      const a = String(from);
      const b = String(to);
      queryFilter.and.push({
        subscriptionExpiryDateAt: { between: a <= b ? [a, b] : [b, a] },
      });
    } else if (!isUndefined(from)) {
      queryFilter.and.push({
        subscriptionExpiryDateAt: { gt: from },
      });
    } else if (!isUndefined(to)) {
      queryFilter.and.push({
        subscriptionExpiryDateAt: { lt: to },
      });
    }
  }

  // 11) 保守期限 maintenancePeriod: between / gt / lt
  if (!isUndefined(argFilter.maintenancePeriodGt) || !isUndefined(argFilter.maintenancePeriodLt)) {
    const from = argFilter.maintenancePeriodGt;
    const to = argFilter.maintenancePeriodLt;

    if (!isUndefined(from) && !isUndefined(to)) {
      const a = String(from);
      const b = String(to);
      queryFilter.and.push({
        maintenancePeriod: { between: a <= b ? [a, b] : [b, a] },
      });
    } else if (!isUndefined(from)) {
      queryFilter.and.push({
        maintenancePeriod: { gt: from },
      });
    } else if (!isUndefined(to)) {
      queryFilter.and.push({
        maintenancePeriod: { lt: to },
      });
    }
  }

  // 12) uuid:bind。UUIDがあれば認証済み判断とする
  // -> UUID:認証なし -> eq: ""でOK。containsに値があってもeqが優先される
  // -> UUID:認証あり -> ne: ""（not equals）でOK。
  if (!isUndefined(argFilter.bind)) {
    if (argFilter.bind === '1') {
      // 認証あり
      queryFilter.and.push({
        uuid: {
          ne: '',
        },
      });
    } else if (argFilter.bind === '2') {
      // 認証なし
      queryFilter.and.push({
        uuid: {
          eq: '',
        },
      });
    }
  }

  // and が空なら filter を送らない
  if (queryFilter.and.length > 0) {
    queryArgument.filter = queryFilter;
  }

  const resp = await API.graphql(
    graphqlOperation(ListLicences, queryArgument)
  );

  const data = resp?.data?.listLicences ?? { items: [], nextToken: null };
  return data;
}
